export const ROUTE_PATHS = {
  salesLeads: "/sales/leads",
  salesAgents: "/sales/agents",
  salesFollowUps: "/sales/follow-ups",
  salesReports: "/sales/reports",
  salesDashboard: "/sales/dashboard",
  inventory: "/inventory",
  admin: "/admin",
  support: "/support",
};

const STATIC_LABEL_ROUTES = {
  "My Leads": ROUTE_PATHS.salesLeads,
  Leads: ROUTE_PATHS.salesLeads,
  "My Agents": ROUTE_PATHS.salesAgents,
  "Follow-ups": ROUTE_PATHS.salesFollowUps,
  Reports: ROUTE_PATHS.salesReports,
  Dashboard: ROUTE_PATHS.salesDashboard,
  Inventory: ROUTE_PATHS.inventory,
  "CRM Admin": ROUTE_PATHS.admin,
  Support: ROUTE_PATHS.support,
};

const STATIC_ROUTE_LABELS = new Map(
  Object.entries(STATIC_LABEL_ROUTES).map(([label, path]) => [path, label]),
);

export function slugifyLabel(label) {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function findLabelBySlug(labels, slug) {
  return labels.find((label) => slugifyLabel(label) === slug) || null;
}

export function getTopNavForLabel(label) {
  if (label === "Inventory") {
    return "Inventory";
  }
  if (label === "CRM Admin") {
    return "CRM Admin";
  }
  if (label === "Support") {
    return "Support";
  }
  return "Sales";
}

export function getPathForLabel(label, options = {}) {
  const { workspaceItems = [], managementItems = [] } = options;

  if (STATIC_LABEL_ROUTES[label]) {
    return STATIC_LABEL_ROUTES[label];
  }

  const inManagement = managementItems.includes(label) && !workspaceItems.includes(label);
  const basePath = inManagement ? "/management" : "/workspace";
  return `${basePath}/${slugifyLabel(label)}`;
}

export function getDefaultPathForUser(user) {
  const workspaceItems = user?.role_scope?.workspace || [];
  const managementItems = user?.role_scope?.management || [];
  const defaultLabel = workspaceItems[0] || managementItems[0] || "My Leads";
  return getPathForLabel(defaultLabel, { workspaceItems, managementItems });
}

export function getPathForTopNav(label, options = {}) {
  const { workspaceItems = [], managementItems = [] } = options;

  if (label === "Inventory") {
    return getPathForLabel("Inventory", { workspaceItems, managementItems });
  }
  if (label === "CRM Admin") {
    return getPathForLabel("CRM Admin", { workspaceItems, managementItems });
  }
  if (label === "Support") {
    return getPathForLabel("Support", { workspaceItems, managementItems });
  }

  const defaultSalesLabel =
    workspaceItems.find((item) => getTopNavForLabel(item) === "Sales") ||
    managementItems.find((item) => getTopNavForLabel(item) === "Sales") ||
    "My Leads";
  return getPathForLabel(defaultSalesLabel, { workspaceItems, managementItems });
}

export function resolveRouteState(pathname, options = {}) {
  const {
    workspaceItems = [],
    managementItems = [],
  } = options;

  const fallbackLabel = workspaceItems[0] || managementItems[0] || "My Leads";

  if (!pathname || pathname === "/") {
    return {
      activeSidebarItem: fallbackLabel,
      activeTopNav: getTopNavForLabel(fallbackLabel),
      isValid: false,
    };
  }

  const staticLabel = STATIC_ROUTE_LABELS.get(pathname);
  if (staticLabel) {
    return {
      activeSidebarItem: staticLabel,
      activeTopNav: getTopNavForLabel(staticLabel),
      isValid: true,
    };
  }

  const workspaceMatch = pathname.match(/^\/workspace\/([^/]+)\/?$/);
  if (workspaceMatch) {
    const matchedLabel =
      findLabelBySlug(workspaceItems, workspaceMatch[1]) ||
      findLabelBySlug(managementItems, workspaceMatch[1]);
    if (matchedLabel) {
      return {
        activeSidebarItem: matchedLabel,
        activeTopNav: getTopNavForLabel(matchedLabel),
        isValid: true,
      };
    }
  }

  const managementMatch = pathname.match(/^\/management\/([^/]+)\/?$/);
  if (managementMatch) {
    const matchedLabel =
      findLabelBySlug(managementItems, managementMatch[1]) ||
      findLabelBySlug(workspaceItems, managementMatch[1]);
    if (matchedLabel) {
      return {
        activeSidebarItem: matchedLabel,
        activeTopNav: getTopNavForLabel(matchedLabel),
        isValid: true,
      };
    }
  }

  return {
    activeSidebarItem: fallbackLabel,
    activeTopNav: getTopNavForLabel(fallbackLabel),
    isValid: false,
  };
}

export function isLeadsContentLabel(label) {
  return !["My Agents", "Follow-ups", "Reports", "Dashboard"].includes(label);
}
